/**
 * useAssistantConversation — the conversation an entity assistant talks in (Spec 64 Phase A): one per form session,
 * opened for where the assistant sits (`entryPoint`) and what it is about (`subjectType` / `subjectId`).
 *
 * It owns the conversation and nothing else. Its messages, the live socket, the merge of the two and sending are the
 * one conversation data layer's (`useConversationStream`), the same one the conversation pages use — this hook used
 * to run a second copy of the message query and the send, without the poll that lets a dropped socket converge.
 */
import type { CoreConversationEntryPoint } from '@confluent/shared/lib/conversation-entry-points';
import { useQuery } from '@tanstack/react-query';
import { conversationsApi } from '../services/api/conversations.api.js';
import type { ConversationMessagePart } from '@confluent/shared/lib/conversation-interactions';
import type { Conversation, ConversationCreate } from '../generated/api/types.gen.js';
import type { StreamMessage } from './useConversationStream.js';

/**
 * A row of the assistant's message list: a view model over the live conversation stream (`StreamMessage`), whose
 * parts keep the shared `ConversationMessagePart` shape the renderer needs. It is not an API payload.
 */
export interface AssistantMessage {
  id: number;
  conversationId: number;
  role: StreamMessage['role'];
  content: string;
  parts?: ConversationMessagePart[];
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Local types — the generated responses are typed as `unknown`; we shape
// them here so the drawer components are fully typed without `any`.
// ---------------------------------------------------------------------------

/**
 * One of core's entry points, or a module page's own `<module>.<name>` (`ConversationEntryPointSchema`, e.g.
 * `finance.strategy-tester`); the server names the persona for it.
 */
export type ConversationEntryPoint = Exclude<CoreConversationEntryPoint, 'home'> | `${string}.${string}`;

// ---------------------------------------------------------------------------
// Hook params
// ---------------------------------------------------------------------------

export interface UseAssistantConversationParams {
  /** What the conversation is about, as the create route takes it. */
  subjectType: NonNullable<ConversationCreate['subjectType']>;
  subjectId?: string;
  draftKey: string;
  /**
   * Where in the app the assistant is opened from. The server names the persona for it — a page never does
   * (`@confluent/shared/lib/conversation-entry-points`).
   */
  entryPoint: ConversationEntryPoint;
  /**
   * `assistant-ui` (default): form help beside an editor. `user-agent`: the persona also acts through the entity skills
   * (reads a run, queues a backtest) — the Strategy Tester's assistant.
   */
  kind?: 'assistant-ui' | 'user-agent';
  currentFormValues: Record<string, unknown>;
  /**
   * The form in the shape the assistant's state hook reads (`components/assistant/assistant-context-spec.ts`).
   * What the conversation is created with; the form values as they are when a caller has no shape to give.
   */
  contextSpec?: Record<string, unknown>;
}

export interface UseAssistantConversationResult {
  conversation: Conversation | null;
  conversationId: number | null;
  isLoading: boolean;
  error: Error | null;
}

function optionalIdString(value: unknown): string | null | undefined {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) return String(value);
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return value.trim();
  if (value === null) return null;
  return undefined;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export function useAssistantConversation({
  subjectType,
  subjectId,
  draftKey,
  entryPoint,
  kind = 'assistant-ui',
  currentFormValues,
  contextSpec,
}: UseAssistantConversationParams): UseAssistantConversationResult {
  // Stable cache key: prefer subjectId when available, fall back to draftKey.
  const convCacheKey = ['assistant-conversation', subjectType, subjectId ?? draftKey, entryPoint, kind];

  // Find-or-create — POST /conversations once per form session, and keep the id. A query (not a mutation) is right
  // here because the call is idempotent: for an `assistant-ui` conversation the server returns the open one it already
  // has for this organization, person and subject (`ConversationService.startConversation`, "dedup for assistant-ui
  // kind"), so a retry or a remount reopens the same conversation instead of creating a second.
  const {
    data: conversation,
    isLoading: isCreating,
    error: createError,
  } = useQuery<Conversation, Error>({
    queryKey: convCacheKey,
    queryFn: async () => {
      const res = await conversationsApi.create({
        body: {
          topic: `Help with ${subjectType}`,
          kind,
          entryPoint,
          subjectType,
          subjectId: subjectId ?? null,
          contextSpec: contextSpec ?? currentFormValues,
          projectId: optionalIdString(currentFormValues.projectId),
        },
      });
      if (res.data === undefined)
        throw res.error instanceof Error ? res.error : new Error('Could not start the assistant conversation');
      return res.data;
    },
    // Only run once per cache key — staleTime = Infinity means we never
    // re-fetch from the server (the ID is stable for this form session).
    staleTime: Number.POSITIVE_INFINITY,
  });

  const conversationId = conversation?.id ?? null;

  return {
    conversation: conversation ?? null,
    conversationId,
    isLoading: isCreating,
    error: createError,
  };
}
